import React, { Component } from 'react'
import { MDBRow, MDBCol, MDBBtn, MDBIcon, MDBDatatable } from 'mdb-react-ui-kit'
import MDB4Datatable from '../../../../components/content/MDB4Datatable/MDB4Datatable.component'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import EliteAgencyTeamService from '../../../../shared/services/EliteAgencyTeam.service'
import CommissionPaymentsService from '../../../../shared/services/CommissionPayments.service'
import './EliteAgencyTeamsTab.component.scss'

class EliteAgencyTeamsTab extends Component {
  constructor(props) {
    super(props)

    const currentDate = new Date()
    const currentYear = currentDate.getFullYear()
    const currentMonth = currentDate.getMonth() + 1

    this.state = {
      eliteAgencyTeams: [],
      selectedTeamId: null,
      selectedYear: currentYear,
      selectedMonth: currentMonth,
      salesData: [],
      loading: false,
      loadingTeams: true,
      uploadingFile: false,
      processingCsv: false,
      uploadedCsvData: null,
      uploadedFilePath: null,
      isInitialLoad: true,
      previewRowCount: 10,
      csvValidationWarnings: [],
      csvProcessingErrors: [],
      pagination: { page: 0, per_page: 10 },
    }

    this.fileInputRef = React.createRef()
  }

  componentDidMount() {
    this.fetchEliteAgencyTeams()
  }

  fetchEliteAgencyTeams = async () => {
    try {
      this.setState({ loadingTeams: true })
      const response = await EliteAgencyTeamService.search()

      // Extract teams array from response - handle different response structures
      let teams = []
      if (Array.isArray(response)) {
        teams = response
      } else if (response?.models && Array.isArray(response.models)) {
        teams = response.models
      } else if (response?.data && Array.isArray(response.data)) {
        teams = response.data
      } else if (response?.payload && Array.isArray(response.payload)) {
        teams = response.payload
      }

      // Find Team Banville (id: 2) and set as default
      const teamBanville = teams.find(team => team.id === 2)
      const defaultTeamId = teamBanville ? teamBanville.id : teams.length > 0 ? teams[0].id : null

      this.setState(
        {
          eliteAgencyTeams: teams,
          selectedTeamId: defaultTeamId,
          loadingTeams: false,
        },
        () => {
          // Automatically fetch sales data for default team
          if (defaultTeamId) {
            this.fetchSalesData()
          }
        }
      )
    } catch (error) {
      console.error('Error fetching Elite Agency Teams:', error)
      toast.error('Failed to load Elite Agency Teams')
      this.setState({ loadingTeams: false })
    }
  }

  fetchSalesData = async () => {
    const { selectedTeamId, selectedYear, selectedMonth, isInitialLoad } = this.state

    if (!selectedTeamId) {
      toast.warning('Please select a Elite Agency Team')
      return
    }

    try {
      this.setState({ loading: true })
      const response = await CommissionPaymentsService.getCareerSales(selectedTeamId, selectedYear, selectedMonth)

      this.setState({
        salesData: response.data || [],
        loading: false,
        isInitialLoad: false,
      })

      if (!response.data || response.data.length === 0) {
        // Only show toast if not the initial load
        if (!isInitialLoad) {
          toast.info('No sales data found for the selected period')
        }
      }
    } catch (error) {
      console.error('Error fetching sales data:', error)
      toast.error('Failed to load sales data')
      this.setState({ loading: false, isInitialLoad: false })
    }
  }

  handleTeamChange = e => {
    this.setState({ selectedTeamId: parseInt(e.target.value) }, () => {
      this.fetchSalesData()
    })
  }

  handleYearChange = e => {
    this.setState({ selectedYear: parseInt(e.target.value) }, () => {
      this.fetchSalesData()
    })
  }

  handleMonthChange = e => {
    this.setState({ selectedMonth: parseInt(e.target.value) }, () => {
      this.fetchSalesData()
    })
  }

  handleDownloadCsv = async () => {
    const { selectedTeamId, selectedYear, selectedMonth } = this.state

    if (!selectedTeamId) {
      toast.warning('Please select a Elite Agency Team')
      return
    }

    try {
      toast.info('Generating CSV file...')
      const response = await CommissionPaymentsService.downloadCsv(selectedTeamId, selectedYear, selectedMonth)

      if (response.data && response.data.url) {
        // Open download URL in new tab
        window.open(response.data.url, '_blank')
        toast.success('CSV file generated successfully')
      } else {
        toast.error('Failed to generate CSV file')
      }
    } catch (error) {
      console.error('Error downloading CSV:', error)
      toast.error('Failed to generate CSV file')
    }
  }

  handleUploadCsv = () => {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.click()
    }
  }

  handleFileSelect = async e => {
    const file = e.target.files[0]
    if (!file) return

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      toast.error('Please select a valid CSV file')
      return
    }

    try {
      this.setState({
        uploadingFile: true,
        uploadedCsvData: null,
        uploadedFilePath: null,
        csvValidationWarnings: [],
        csvProcessingErrors: [],
      })
      toast.info('Uploading CSV file...')

      // Upload file to storage first
      const uploadResponse = await CommissionPaymentsService.uploadFile(file)

      // Handle different response structures
      let filePath = null
      if (uploadResponse?.payload?.file_path) {
        filePath = uploadResponse.payload.file_path
      } else if (uploadResponse?.file_path) {
        filePath = uploadResponse.file_path
      } else if (uploadResponse?.data?.file_path) {
        filePath = uploadResponse.data.file_path
      } else if (typeof uploadResponse === 'string') {
        filePath = uploadResponse
      }

      if (!filePath) {
        throw new Error('File upload failed - could not extract file path from response')
      }

      toast.success('CSV file uploaded successfully')

      // Parse the CSV file locally to show preview
      const { data, warnings } = await this.parseCSVFile(file)

      this.setState({
        uploadingFile: false,
        uploadedCsvData: data,
        uploadedFilePath: filePath,
        previewRowCount: 10,
        csvValidationWarnings: warnings,
      })

      if (warnings.length > 0) {
        toast.warning(`CSV uploaded with ${warnings.length} validation warning(s). Please review below.`, {
          autoClose: 8000,
        })
      } else {
        toast.info('Review the commission changes below and click "Submit Commission Changes" to apply them')
      }
    } catch (error) {
      console.error('Error uploading CSV:', error)
      toast.error(`Failed to upload CSV file: ${error.message || error}`)
      this.setState({
        uploadingFile: false,
        uploadedCsvData: null,
        uploadedFilePath: null,
        csvValidationWarnings: [],
      })
    }

    // Reset file input
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = ''
    }
  }

  parseCSVLine = line => {
    const result = []
    let current = ''
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
      const char = line[i]
      const nextChar = line[i + 1]

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          current += '"'
          i++ // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        result.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }

    // Add last field
    result.push(current.trim())
    return result
  }

  parseCSVFile = async file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = e => {
        try {
          const text = e.target.result
          const lines = text.split('\n').filter(line => line.trim())

          if (lines.length < 2) {
            reject(new Error('CSV file is empty or invalid'))
            return
          }

          // Parse header
          const headers = this.parseCSVLine(lines[0])

          // Validate that we have the required 'AV ID' column
          if (!headers.includes('AV ID')) {
            reject(new Error('CSV file is missing required "AV ID" column. Please download the template first.'))
            return
          }

          // Parse rows and validate
          const data = []
          const warnings = []

          for (let i = 1; i < lines.length; i++) {
            const values = this.parseCSVLine(lines[i])
            const row = {}
            headers.forEach((header, index) => {
              row[header] = values[index] || ''
            })

            // Check if row has AV ID (required field)
            const avId = row['AV ID']
            if (!avId || avId.trim() === '') {
              warnings.push(`Row ${i + 1}: Missing AV ID`)
              continue // Skip rows without AV ID
            }

            // Validate commission value if provided
            const commPaid = row['Commission Paid']
            if (commPaid && commPaid.trim() !== '' && (isNaN(parseFloat(commPaid)) || parseFloat(commPaid) < 0)) {
              warnings.push(`Row ${i + 1}: Invalid commission value "${commPaid}" for AV ID ${avId}`)
            }

            data.push(row)
          }

          resolve({ data, warnings })
        } catch (err) {
          reject(err)
        }
      }

      reader.onerror = () => reject(new Error('Failed to read CSV file'))
      reader.readAsText(file)
    })
  }

  handleSubmitCommissions = async () => {
    const { uploadedFilePath } = this.state

    if (!uploadedFilePath) {
      toast.error('No CSV file uploaded')
      return
    }

    try {
      this.setState({ processingCsv: true })
      toast.info('Processing commission changes...')

      const processResponse = await CommissionPaymentsService.processCsv(uploadedFilePath)

      if (processResponse.apistatus) {
        toast.success(processResponse.message || 'Commission payments updated successfully')
        // Clear uploaded data and refresh sales
        this.setState({
          uploadedCsvData: null,
          uploadedFilePath: null,
          processingCsv: false,
          previewRowCount: 10,
          csvValidationWarnings: [],
          csvProcessingErrors: [],
        })
        this.fetchSalesData()
      } else {
        // Handle detailed error messages
        let errorMessage = processResponse.message || 'Failed to process CSV file'
        let errors = []

        if (processResponse.errors && Array.isArray(processResponse.errors)) {
          errors = processResponse.errors

          // Show first few errors in toast
          const errorPreview = errors.slice(0, 2).join('; ')
          const additionalErrors = errors.length > 2 ? ` (and ${errors.length - 2} more errors)` : ''

          errorMessage = `${errorMessage}: ${errorPreview}${additionalErrors}. See error details below.`

          toast.error(errorMessage, { autoClose: 10000 })
        } else {
          toast.error(errorMessage)
        }

        this.setState({
          processingCsv: false,
          csvProcessingErrors: errors,
        })
      }
    } catch (error) {
      console.error('Error processing CSV:', error)

      // Extract error details from response if available
      let errorMessage = 'Failed to process CSV file'
      let errors = []

      if (error.response && error.response.data) {
        const responseData = error.response.data
        errorMessage = responseData.message || errorMessage

        // Check for detailed errors array
        if (responseData.errors && Array.isArray(responseData.errors)) {
          errors = responseData.errors

          const errorPreview = errors.slice(0, 2).join('; ')
          const additionalErrors = errors.length > 2 ? ` (and ${errors.length - 2} more errors)` : ''

          errorMessage = `${errorMessage}: ${errorPreview}${additionalErrors}. See error details below.`
        }
      } else if (error.message) {
        errorMessage = `${errorMessage}: ${error.message}`
      }

      toast.error(errorMessage, { autoClose: 10000 })
      this.setState({
        processingCsv: false,
        csvProcessingErrors: errors,
      })
    }
  }

  handleCancelUpload = () => {
    this.setState({
      uploadedCsvData: null,
      uploadedFilePath: null,
      previewRowCount: 10,
      csvValidationWarnings: [],
      csvProcessingErrors: [],
    })
    toast.info('CSV upload cancelled')
  }

  handleLoadMorePreview = () => {
    this.setState(prevState => ({
      previewRowCount: prevState.previewRowCount + 20,
    }))
  }

  handleShowAllPreview = () => {
    const { uploadedCsvData } = this.state
    this.setState({
      previewRowCount: uploadedCsvData.length,
    })
  }

  formatCurrency = value => {
    if (value === null || value === undefined || value === '') return '-'
    const num = parseFloat(value)
    if (isNaN(num)) return '-'
    return `$${num.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`
  }

  formatDate = dateString => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })
  }

  getTableData = () => {
    const { salesData } = this.state

    const columns = [
      {
        label: 'AV ID',
        field: 'av_id',
        sort: 'asc',
        width: 80,
      },
      {
        label: 'Career Agent',
        field: 'career_agent_name',
        sort: 'asc',
        width: 150,
      },
      {
        label: 'Selling Agent',
        field: 'agent_name',
        sort: 'asc',
        width: 150,
      },
      {
        label: 'Submission Date',
        field: 'av_date',
        sort: 'asc',
        width: 120,
      },
      {
        label: 'Effective Date',
        field: 'av_eff_date',
        sort: 'asc',
        width: 120,
      },
      {
        label: 'Client',
        field: 'client_name',
        sort: 'asc',
        width: 150,
      },
      {
        label: 'State',
        field: 'av_state',
        sort: 'asc',
        width: 80,
      },
      {
        label: 'Carrier',
        field: 'av_carrier',
        sort: 'asc',
        width: 150,
      },
      {
        label: 'Product',
        field: 'av_product',
        sort: 'asc',
        width: 150,
      },
      {
        label: 'App ID',
        field: 'av_appid',
        sort: 'asc',
        width: 120,
      },
      {
        label: 'Premium',
        field: 'av_premium',
        sort: 'asc',
        width: 100,
      },
      {
        label: 'AV Total',
        field: 'av_total',
        sort: 'asc',
        width: 100,
      },
      {
        label: 'Points',
        field: 'av_points',
        sort: 'asc',
        width: 80,
      },
      {
        label: 'Status',
        field: 'av_status',
        sort: 'asc',
        width: 120,
      },
      {
        label: 'Commission Paid',
        field: 'comm_paid',
        sort: 'asc',
        width: 120,
      },
    ]

    const rows = salesData.map(sale => ({
      av_id: sale.av_id,
      career_agent_name: sale.career_agent_name || '-',
      agent_name: sale.agent_name || '-',
      av_date: this.formatDate(sale.av_date),
      av_eff_date: this.formatDate(sale.av_eff_date),
      client_name: `${sale.av_client_fname} ${sale.av_client_lname}`,
      av_state: sale.av_state || '-',
      av_carrier: sale.av_carrier || '-',
      av_product: sale.av_product || '-',
      av_appid: sale.av_appid || '-',
      av_premium: this.formatCurrency(sale.av_premium),
      av_total: this.formatCurrency(sale.av_total),
      av_points: sale.av_points || '-',
      av_status: sale.av_status || '-',
      comm_paid: this.formatCurrency(sale.comm_paid),
    }))

    return { columns, rows }
  }

  generateYearOptions = () => {
    const currentYear = new Date().getFullYear()
    const years = []
    // Generate options for current year and 2 years back
    for (let i = 0; i < 3; i++) {
      years.push(currentYear - i)
    }
    return years
  }

  generateMonthOptions = () => {
    return [
      { value: 1, label: 'January' },
      { value: 2, label: 'February' },
      { value: 3, label: 'March' },
      { value: 4, label: 'April' },
      { value: 5, label: 'May' },
      { value: 6, label: 'June' },
      { value: 7, label: 'July' },
      { value: 8, label: 'August' },
      { value: 9, label: 'September' },
      { value: 10, label: 'October' },
      { value: 11, label: 'November' },
      { value: 12, label: 'December' },
    ]
  }

  render() {
    const {
      eliteAgencyTeams,
      selectedTeamId,
      selectedYear,
      selectedMonth,
      salesData,
      loading,
      loadingTeams,
      uploadingFile,
      processingCsv,
      uploadedCsvData,
      uploadedFilePath,
      csvValidationWarnings,
      csvProcessingErrors,
    } = this.state

    if (loadingTeams) {
      return (
        <div className='loading-container'>
          <MDBIcon icon='spinner' spin size='2x' className='mr-2' />
          Loading Elite Agency Teams...
        </div>
      )
    }

    const tableData = this.getTableData()

    return (
      <div className='elite-agency-teams-tab'>
        <MDBRow className='mb-4'>
          <MDBCol md='12'>
            <h4 className='section-title'>Career Agent Commission Payments</h4>
            <p className='section-description'>
              Select a Elite Agency Team and date range to view Career agent sales. Download the CSV, add commission
              values, and re-upload to update commission payments. The Effective Date is shown for reference only.
            </p>
          </MDBCol>
        </MDBRow>

        <MDBRow className='mb-4'>
          <MDBCol md='4'>
            <label htmlFor='teamSelect' className='filter-label'>
              Elite Agency Team
            </label>
            <select
              id='teamSelect'
              className='browser-default custom-select'
              value={selectedTeamId || ''}
              onChange={this.handleTeamChange}
              disabled={loading}
            >
              <option value=''>Select a team...</option>
              {eliteAgencyTeams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.team_name}
                </option>
              ))}
            </select>
          </MDBCol>

          <MDBCol md='3'>
            <label htmlFor='yearSelect' className='filter-label'>
              Year
            </label>
            <select
              id='yearSelect'
              className='browser-default custom-select'
              value={selectedYear}
              onChange={this.handleYearChange}
              disabled={loading}
            >
              {this.generateYearOptions().map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </MDBCol>

          <MDBCol md='3'>
            <label htmlFor='monthSelect' className='filter-label'>
              Month
            </label>
            <select
              id='monthSelect'
              className='browser-default custom-select'
              value={selectedMonth}
              onChange={this.handleMonthChange}
              disabled={loading}
            >
              {this.generateMonthOptions().map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </MDBCol>

          <MDBCol md='2' className='d-flex align-items-end'>
            <MDBBtn
              type='button'
              className='btn-refresh'
              color='primary'
              size='sm'
              onClick={this.fetchSalesData}
              disabled={loading || !selectedTeamId}
              block
            >
              {loading ? (
                <>
                  <MDBIcon icon='spinner' spin className='mr-1' />
                  Loading...
                </>
              ) : (
                <>
                  <MDBIcon icon='sync' className='mr-1' />
                  Refresh
                </>
              )}
            </MDBBtn>
          </MDBCol>
        </MDBRow>

        <MDBRow className='mb-3'>
          <MDBCol md='12' className='commission-csv-buttons'>
            <MDBBtn
              type='button'
              className='btn-download-csv'
              color='success'
              size='sm'
              onClick={this.handleDownloadCsv}
              disabled={loading || !selectedTeamId || salesData.length === 0}
            >
              <MDBIcon icon='download' className='mr-1' />
              Download CSV
            </MDBBtn>
            <MDBBtn
              type='button'
              className='btn-upload-csv'
              color='info'
              size='sm'
              onClick={this.handleUploadCsv}
              disabled={uploadingFile || !selectedTeamId}
            >
              {uploadingFile ? (
                <>
                  <MDBIcon icon='spinner' spin className='mr-1' />
                  Uploading...
                </>
              ) : (
                <>
                  <MDBIcon icon='upload' className='mr-1' />
                  Upload CSV
                </>
              )}
            </MDBBtn>
            <input
              type='file'
              ref={this.fileInputRef}
              style={{ display: 'none' }}
              accept='.csv'
              onChange={this.handleFileSelect}
            />
          </MDBCol>
        </MDBRow>

        {/* CSV Validation Warnings Section */}
        {csvValidationWarnings && csvValidationWarnings.length > 0 && (
          <MDBRow className='mt-4 mb-3'>
            <MDBCol md='12'>
              <div className='alert alert-warning' role='alert'>
                <h6 className='alert-heading'>
                  <MDBIcon icon='exclamation-triangle' className='mr-2' />
                  CSV Validation Warnings ({csvValidationWarnings.length})
                </h6>
                <p className='mb-2'>
                  The following issues were detected in your CSV file. Rows with missing AV IDs have been skipped:
                </p>
                <ul className='mb-0' style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {csvValidationWarnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            </MDBCol>
          </MDBRow>
        )}

        {/* CSV Processing Errors Section */}
        {csvProcessingErrors && csvProcessingErrors.length > 0 && (
          <MDBRow className='mt-4 mb-3'>
            <MDBCol md='12'>
              <div className='alert alert-danger' role='alert'>
                <h6 className='alert-heading'>
                  <MDBIcon icon='times-circle' className='mr-2' />
                  CSV Processing Errors ({csvProcessingErrors.length})
                </h6>
                <p className='mb-2'>The following errors prevented processing of your CSV file:</p>
                <ul className='mb-0' style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {csvProcessingErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </MDBCol>
          </MDBRow>
        )}

        {/* CSV Preview Section */}
        {uploadedCsvData && uploadedCsvData.length > 0 && (
          <MDBRow className='mt-4 mb-4'>
            <MDBCol md='12'>
              <div className='csv-preview-section'>
                <div className='csv-preview-header'>
                  <h5>
                    <MDBIcon icon='file-csv' className='mr-2' />
                    CSV Upload Preview ({uploadedCsvData.length} rows)
                  </h5>
                  <div>
                    <MDBBtn
                      type='button'
                      color='danger'
                      size='sm'
                      onClick={this.handleCancelUpload}
                      disabled={processingCsv}
                      className='mr-2'
                    >
                      <MDBIcon icon='times' className='mr-1' />
                      Cancel
                    </MDBBtn>
                    <MDBBtn
                      type='button'
                      color='primary'
                      size='sm'
                      onClick={this.handleSubmitCommissions}
                      disabled={processingCsv}
                    >
                      {processingCsv ? (
                        <>
                          <MDBIcon icon='spinner' spin className='mr-1' />
                          Processing...
                        </>
                      ) : (
                        <>
                          <MDBIcon icon='check' className='mr-1' />
                          Submit Commission Changes
                        </>
                      )}
                    </MDBBtn>
                  </div>
                </div>
                <div className='csv-preview-table'>
                  <table className='table table-sm table-striped table-bordered'>
                    <thead>
                      <tr>
                        {Object.keys(uploadedCsvData[0]).map((header, idx) => (
                          <th key={idx}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {uploadedCsvData.slice(0, this.state.previewRowCount).map((row, rowIdx) => (
                        <tr key={rowIdx}>
                          {Object.values(row).map((cell, cellIdx) => (
                            <td key={cellIdx}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {uploadedCsvData.length > this.state.previewRowCount && (
                    <div className='text-center mt-3'>
                      <p className='text-muted mb-2'>
                        Showing {this.state.previewRowCount} of {uploadedCsvData.length} rows
                      </p>
                      <MDBBtn
                        type='button'
                        color='info'
                        size='sm'
                        onClick={this.handleLoadMorePreview}
                        className='mr-2'
                      >
                        <MDBIcon icon='plus' className='mr-1' />
                        Load 20 More
                      </MDBBtn>
                      <MDBBtn type='button' color='secondary' size='sm' onClick={this.handleShowAllPreview}>
                        <MDBIcon icon='eye' className='mr-1' />
                        Show All ({uploadedCsvData.length} rows)
                      </MDBBtn>
                    </div>
                  )}
                  {uploadedCsvData.length <= this.state.previewRowCount && uploadedCsvData.length > 10 && (
                    <div className='text-muted text-center mt-2'>Showing all {uploadedCsvData.length} rows</div>
                  )}
                </div>
              </div>
            </MDBCol>
          </MDBRow>
        )}

        <MDBRow>
          <MDBCol md='12'>
            {loading ? (
              <div className='loading-container'>
                <MDBIcon icon='spinner' spin size='2x' className='mr-2' />
                Loading sales data...
              </div>
            ) : salesData.length === 0 ? (
              <div className='no-data-message'>
                <MDBIcon icon='info-circle' className='mr-2' />
                No sales data available for the selected period.
              </div>
            ) : (
              <>
                <MDB4Datatable
                  data={tableData}
                  striped
                  bordered
                  hover
                  responsive
                  entries={25}
                  entriesOptions={[10, 25, 50, 100]}
                  paging={salesData.length > 10}
                  searching={true}
                  sortable={true}
                  noBottomColumns
                />
              </>
            )}
          </MDBCol>
        </MDBRow>
      </div>
    )
  }
}

export default EliteAgencyTeamsTab
